// /src/components/PurchaseStockForm.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaCheck, FaList, FaPlus, FaUndo } from "react-icons/fa";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Select from "react-select";
import {
  defaultStoneFields,
  purchaseStockFields,
  purchaseStockTopFields,
} from "../config/fieldsHelper";
import { handleDropDowns } from "../config/dropDownHelpers";
import { useDispatch, useSelector } from "react-redux";
import {
  convertToDateInputFormat,
  getTodayDate,
  threeDecimal,
  twoDecimal,
} from "../config/helperFunctions";
import toast from "react-hot-toast";
import MultiplierStock from "./MultiplierStock";
import PaymentSection from "./other/PaymentSection";
import FloatingTotals from "./purchase/FloatingTotals";
import { useAddPurchaseMutation } from "../toolkitServices/purchaseApi";
import PurchaseStockItemForm from "./purchase/PurchaseStockItemForm";

/* ---------------- helpers for payload cleanup ---------------- */
const isBlank = (v) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "");
const keepStrOrEmpty = (v) => (isBlank(v) ? "" : String(v).trim());
const keepBool = (v) => !!v;

const isStockRowMeaningful = (r = {}) => {
  const keysToCheck = [
    "totalWt",
    "packingWt",
    "grossWt",
    "stoneWt",
    "diamondWt",
    "netWt",
    "qty",
    "pcs",
    "stonePcs",
    "diamondPcs",
    "ratePerGram",
    "finePercent",
    "makingPercentage",
    "makingPerGram",
    "fixedMaking",
    "fixedAmount",
    "totalStoneAmount",
    "totalDiamondAmount",
    "hallmarkCharge",
    "totalItemMakingAmount",
    "totalItemAmount",
    "itemFineWt",
    "itemMakingWt",
    "itemFineMakingWt",
  ];
  const hasNums = keysToCheck.find((k) => !isBlank(r[k])) !== undefined;
  const hasEntity =
    r?.category?.value || r?.product?.value || r?.design?.value || r?.purity?.value;
  const hasStones =
    Array.isArray(r?.stones) &&
    r.stones.some(
      (s) =>
        !isBlank(s?.stnPopupName) ||
        !isBlank(s?.stnPopupWt) ||
        !isBlank(s?.stnPopupPcs) ||
        !isBlank(s?.stnPopupAmt)
    );
  return !!(hasNums || hasEntity || hasStones);
};

const normalizeStockRow = (row = {}) => ({
  totalWt: keepStrOrEmpty(row.totalWt),
  packingWt: keepStrOrEmpty(row.packingWt),
  grossWt: isBlank(row.grossWt) ? "" : row.grossWt,
  stoneWt: keepStrOrEmpty(row.stoneWt),
  diamondWt: keepStrOrEmpty(row.diamondWt),
  netWt: isBlank(row.netWt) ? "" : row.netWt,
  qty: keepStrOrEmpty(row.qty),
  pcs: keepStrOrEmpty(row.pcs),
  stonePcs: keepStrOrEmpty(row.stonePcs),
  diamondPcs: keepStrOrEmpty(row.diamondPcs),
  ratePerGram: keepStrOrEmpty(row.ratePerGram),
  finePercent: keepStrOrEmpty(row.finePercent),
  makingPercentage: keepStrOrEmpty(row.makingPercentage),
  makingPerGram: keepStrOrEmpty(row.makingPerGram),
  fixedMaking: keepStrOrEmpty(row.fixedMaking),
  fixedAmount: keepStrOrEmpty(row.fixedAmount),
  totalStoneAmount: keepStrOrEmpty(row.totalStoneAmount),
  totalDiamondAmount: keepStrOrEmpty(row.totalDiamondAmount),
  hallmarkCharge: keepStrOrEmpty(row.hallmarkCharge),
  totalItemMakingAmount: keepStrOrEmpty(row.totalItemMakingAmount),
  totalItemAmount: keepStrOrEmpty(row.totalItemAmount),
  itemFineWt: keepStrOrEmpty(row.itemFineWt),
  itemMakingWt: keepStrOrEmpty(row.itemMakingWt),
  itemFineMakingWt: keepStrOrEmpty(row.itemFineMakingWt),
  rateCut: keepBool(row.rateCut),
  category: row.category || null,
  product: row.product || null,
  design: row.design || null,
  purity: row.purity || null,
  stones: Array.isArray(row.stones)
    ? row.stones.map((s) => ({
        stnPopupName: keepStrOrEmpty(s?.stnPopupName),
        stnPopupWt: keepStrOrEmpty(s?.stnPopupWt),
        stnPopupRateByWt: keepStrOrEmpty(s?.stnPopupRateByWt),
        stnPopupPcs: keepStrOrEmpty(s?.stnPopupPcs),
        stnPopupRateByPcs: keepStrOrEmpty(s?.stnPopupRateByPcs),
        stnPopupAmt: keepStrOrEmpty(s?.stnPopupAmt),
        stnPopupDeduct: keepStrOrEmpty(s?.stnPopupDeduct),
        stnPopupDescription: keepStrOrEmpty(s?.stnPopupDescription),
      }))
    : [],
});

const normalizePayment = (p = {}) => ({
  paymentType: keepStrOrEmpty(p.paymentType),
  paymentMethod: keepStrOrEmpty(p.paymentMethod),
  paidAmount: keepStrOrEmpty(p.paidAmount),
  remarks: keepStrOrEmpty(p.remarks),
});
/* -------------------------------------------------------------- */

/* ✅ single source of truth for clean resets */
const DEFAULTS = {
  vendor: null,
  branch: null,
  invoiceNo: "",
  purchasingDate: convertToDateInputFormat(getTodayDate()),
  stockEntries: [{ totalWt: "", stones: [defaultStoneFields] }],
  discount: "",
  courierCharge: "",
  gstApply: false,
  gstRoundOff: false,
  payments: [],
  fineGold: "",
  fineSilver: "",
  advanceAmount: "",
  balanceAmount: "",
};

const PurchaseStockForm = ({ existingData = null }) => {
  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const vendorsFromState = useSelector((s) => s.vendors?.vendors);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  const [isMultiplierOpen, setIsMultiplierOpen] = useState(false);

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    shouldUnregister: true,
    defaultValues: existingData || DEFAULTS,
  });

  const [addPurchase, { isLoading }] = useAddPurchaseMutation();

  const { fields, append, remove } = useFieldArray({ control, name: "stockEntries" });
  const scrollFrameRef = useRef(null);
  const rowRefs = useRef([]);
  const [activeRow, setActiveRow] = useState(0);
  const [summaryTotals, setSummaryTotals] = useState({
    totalMetal: 0,
    totalAmount: 0,
    gstAmount: 0,
    tcsAmount: 0,
    paidAmount: 0,
    balanceMetal: 0,
    payableAmount: 0,
    balancePayable: 0,
  });
  const firstFieldRef = useRef(null);

  const gstApply = watch("gstApply");
  const gstRoundOff = watch("gstRoundOff");
  const invoiceNo = watch("invoiceNo");
  const payments = watch("payments");
  const items = watch("stockEntries");

  const scrollRowIntoView = (rowIndex) => {
    const frame = scrollFrameRef.current;
    const rowEl = rowRefs.current[rowIndex];
    if (!frame || !rowEl) return;
    try {
      rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {
      const frameRect = frame.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();
      const current = frame.scrollTop;
      const offset =
        rowRect.top + rowRect.height / 2 - (frameRect.top + frameRect.height / 2);
      frame.scrollTo({ top: current + offset, behavior: "smooth" });
    }
  };

  // keyboard helpers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "+") {
        e.preventDefault();
        append({ totalWt: "", stones: [defaultStoneFields] });
        requestAnimationFrame(() => setTimeout(() => firstFieldRef.current?.focus(), 0));
      }
      if (e.ctrlKey && e.key === "r") {
        e.preventDefault();
        reset(DEFAULTS);
        requestAnimationFrame(() => setTimeout(() => firstFieldRef.current?.focus(), 0));
      }
      if (e.ctrlKey && e.key === "*") {
        e.preventDefault();
        setIsMultiplierOpen(true);
      }
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [append, reset, handleSubmit]);

  useEffect(() => {
    scrollRowIntoView(activeRow);
  }, [activeRow, fields?.length]);

  // floating totals
  useEffect(() => {
    const entries = getValues("stockEntries") || [];
    const discount = parseFloat(getValues("discount") || 0);
    const courierCharge = parseFloat(getValues("courierCharge") || 0);

    let totalMetal = 0;
    let totalAmount = 0;
    let paidMetal = 0;

    for (let entry of entries) {
      const amt = parseFloat(entry?.totalItemAmount || 0);
      const metal = parseFloat(entry?.itemFineMakingWt || 0);
      totalMetal += metal;
      totalAmount += amt;
    }

    const gst = gstApply ? +(totalAmount * 0.03).toFixed(2) : 0;
    const tcs = totalAmount >= 20000000 ? +(totalAmount * 0.001).toFixed(2) : 0;
    const balanceMetal = +(totalMetal - paidMetal).toFixed(3);

    const payableAmount = +(
      totalAmount +
      gst +
      tcs -
      discount +
      courierCharge
    ).toFixed(2);

    const paymentsArr = getValues("payments") || [];
    const { paid, received } = paymentsArr.reduce(
      (acc, p) => {
        const amt = parseFloat(p?.paidAmount) || 0;
        if ((p?.paymentType || "paid") === "received") acc.received += amt;
        else acc.paid += amt;
        return acc;
      },
      { paid: 0, received: 0 }
    );

    const netPaid = +(paid - received).toFixed(2);
    const balancePayable = +(payableAmount - netPaid).toFixed(2);

    setSummaryTotals({
      totalMetal: +totalMetal.toFixed(3),
      totalAmount: +totalAmount.toFixed(2),
      gstAmount: gst,
      tcsAmount: tcs,
      paidAmount: netPaid,
      balanceMetal,
      payableAmount,
      balancePayable,
    });
  }, [
    fields,
    watch("stockEntries"),
    watch("discount"),
    watch("courierCharge"),
    watch("gstApply"),
    watch("payments"),
  ]);

  const duplicateFirstEntry = (n = 0) => {
    const entries = getValues("stockEntries") || [];
    if (!entries.length || n < 1) return;
    const first = entries[0];
    const makeClone = () => {
      const c =
        typeof structuredClone === "function"
          ? structuredClone(first)
          : JSON.parse(JSON.stringify(first));
      delete c.itemCode;
      delete c.id;
      return c;
    };
    const clones = Array.from({ length: n }, makeClone);
    append(clones, { shouldFocus: true });
  };

  const onSubmit = async (form) => {
    // Build payload for backend
    const rawRows = Array.isArray(form.stockEntries) ? form.stockEntries : [];
    const stockEntries = rawRows.filter(isStockRowMeaningful).map(normalizeStockRow);

    const rawPayments = Array.isArray(form.payments) ? form.payments : [];
    const payments = rawPayments
      .map(normalizePayment)
      .filter((p) => !isBlank(p.paymentType) || !isBlank(p.paidAmount));

    const payload = {
      invoiceNo: keepStrOrEmpty(form.invoiceNo),
      purchasingDate: keepStrOrEmpty(form.purchasingDate),
      fineGold: keepStrOrEmpty(form.fineGold),
      fineSilver: keepStrOrEmpty(form.fineSilver),
      advanceAmount: keepStrOrEmpty(form.advanceAmount),
      balanceAmount: keepStrOrEmpty(form.balanceAmount),
      gstApply: keepBool(form.gstApply),
      gstRoundOff: keepBool(form.gstRoundOff),
      discount: keepStrOrEmpty(form.discount),
      courierCharge: keepStrOrEmpty(form.courierCharge),

      vendor: form.vendor || null,  // {label,value}
      branch: form.branch || null,  // {label,value} if provided

      stockEntries,
      payments,
    };

    try {
      const res = await addPurchase(payload).unwrap();
      toast.success(res?.msg || "Purchase saved!");
      reset(DEFAULTS);
      requestAnimationFrame(() => setTimeout(() => firstFieldRef.current?.focus(), 0));
    } catch (err) {
      const msg =
        err?.data?.msg || err?.data?.message || err?.error || "Error saving purchase!";
      toast.error(msg);
    }
  };

  return (
    <>
      {isMultiplierOpen && (
        <MultiplierStock
          open={isMultiplierOpen}
          setOpen={setIsMultiplierOpen}
          onApply={duplicateFirstEntry}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="relative">
          <div className="grid grid-cols-[auto_repeat(7,minmax(0,1fr))] gap-2 mt-2">
            <div className="mr-6">
              <label className="block text-sm font-medium text-[#4b3d91] mb-1 ">
                Lot No
              </label>
              <p className=" text-sm font-medium flex align-center py-2 text-blue-500">123</p>
            </div>

            {purchaseStockTopFields?.map((item, idx) => (
              <div key={idx}>
                <label className="block text-sm font-medium text-[#4b3d91] mb-1">
                  {item?.label} {item?.mandatory && <span className="text-red-400">*</span>}
                </label>

                {/* TEXT */}
                {item?.type === "text" && (
                  <>
                    <input
                      {...register(item.name, {
                        onChange: (e) => {
                          const value = e.target.value;
                          if (item?.name === "invoiceNo") {
                            if (`${value}`.trim()) {
                              setValue("gstRoundOff", true);
                              setValue("gstApply", true);
                            } else {
                              setValue("gstRoundOff", false);
                              setValue("gstApply", false);
                            }
                          }
                        },
                        required: item?.mandatory ? `${item.label} is required` : false,
                      })}
                      disabled={!item?.visibility || (item?.name === "invoiceNo" && payments?.length)}
                      className={`w-full px-4 py-1.5 border border-[#ccc] rounded-md focus:outline-none focus:ring-2 ${
                        invoiceNo ? "focus:ring-green-400" : "focus:ring-red-400"
                      }`}
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-xs mt-1">{errors[item.name].message}</p>
                    )}
                  </>
                )}

                {/* NUMBER */}
                {item?.type === "number" && (
                  <>
                    <input
                      type="number"
                      {...register(item.name, {
                        required: item?.mandatory ? `${item.label} is required` : false,
                        pattern: {
                          value: /^[a-zA-Z0-9]*$/,
                          message: "Only alphanumeric characters are allowed",
                        },
                      })}
                      placeholder={`Enter ${item.label}`}
                      className={`w-full px-4 py-2 border border-[#ccc] rounded-md focus:outline-none focus:ring-2 ${
                        invoiceNo ? "focus:ring-green-400" : "focus:ring-red-400"
                      }`}
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-xs mt-1">{errors[item.name].message}</p>
                    )}
                  </>
                )}

                {/* DATE */}
                {item?.type === "date" && (
                  <>
                    <input
                      type="date"
                      {...register(item.name, {
                        required: item?.mandatory ? `${item.label} is required` : false,
                      })}
                      className={`w-full px-4 py-1.5 border border-[#ccc] rounded-md focus:outline-none focus:ring-2 ${
                        invoiceNo ? "focus:ring-green-400" : "focus:ring-red-400"
                      }`}
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-xs mt-1">{errors[item.name].message}</p>
                    )}
                  </>
                )}

                {/* DROPDOWN */}
                {item?.type === "dropdown" && (
                  <>
                    <Controller
                      name={item.name}
                      control={control}
                      rules={{
                        required: item?.mandatory ? `${item.label} is required` : false,
                      }}
                      render={({ field: { onChange, ...field } }) => (
                        <Select
                          {...field}
                          options={handleDropDowns({
                            key: item["name"],
                            vendors: vendorsFromState,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            category: "",
                            product: "",
                            branches: branchesFromState,
                          })}
                          classNamePrefix="react-select"
                          menuPlacement="bottom"
                          isClearable
                          onChange={(selectedOption) => {
                            // keep full select object (or null) for backend
                            onChange(selectedOption ?? null);
                            if (item.name === "vendor" && selectedOption?.info) {
                              setValue("vendor", {
                                label: selectedOption?.label,
                                value: selectedOption?.value,
                              });
                              setValue("fineGold", threeDecimal(selectedOption.info.fineGold));
                              setValue("fineSilver", threeDecimal(selectedOption.info.fineSilver));
                              setValue(
                                "advanceAmount",
                                threeDecimal(selectedOption.info.advanceAmount)
                              );
                              setValue(
                                "balanceAmount",
                                threeDecimal(selectedOption.info.balanceAmount)
                              );
                            }
                          }}
                        />
                      )}
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-xs mt-1">{errors[item.name].message}</p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <div
            ref={scrollFrameRef}
            className={`my-2 rounded-md h-[260px] overflow-y-auto py-2 pl-2 bg-gray-100 scroll-smooth border ${
              invoiceNo ? "border-green-400" : "border-red-400"
            }`}
          >
            {fields?.map((item, rowindex) => (
              <div key={item.id} ref={(el) => (rowRefs.current[rowindex] = el)} className="mb-2">
                <PurchaseStockItemForm
                  invoiceNo={invoiceNo}
                  firstFieldRef={firstFieldRef}
                  control={control}
                  fieldId={item.id}
                  watch={watch}
                  labelledStockFields={purchaseStockFields}
                  item={item}
                  rowindex={rowindex}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                  onRowFocus={() => setActiveRow(rowindex)}
                  onDelete={() => {
                    const nextIndex = Math.max(0, rowindex - 1);
                    remove(rowindex);
                    requestAnimationFrame(() => {
                      setTimeout(() => {
                        setActiveRow(Math.min(nextIndex, (fields?.length || 1) - 2));
                        firstFieldRef.current?.focus();
                        scrollRowIntoView(Math.min(nextIndex, (fields?.length || 1) - 2));
                      }, 0);
                    });
                  }}
                />
              </div>
            ))}
          </div>

          <FloatingTotals items={items} />
        </div>

        <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PaymentSection control={control} register={register} watch={watch} invoiceNo={invoiceNo} />

          {/* Calculations */}
          <div
            className={`rounded-md border max-h-[205px] overflow-y-auto ${
              invoiceNo ? "border-green-400" : "border-red-400"
            } bg-white p-1.5 shadow-sm`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-1 gap-x-4 px-2">
              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Total Metal (g)
                </label>
                <input
                  type="number"
                  step="0.001"
                  disabled
                  value={threeDecimal(summaryTotals?.totalMetal)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Total Amount (₹)
                </label>
                <input
                  type="text"
                  disabled
                  value={twoDecimal(summaryTotals?.totalAmount)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Paid Metal (g)
                </label>
                <input
                  type="text"
                  disabled
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex items-center justify-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  GST (3%) Amount (₹)
                </label>
                <div className="relative flex-1">
                  <input
                    type="text"
                    disabled
                    value={twoDecimal(summaryTotals?.gstAmount)}
                    className="w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                    placeholder="0.000"
                  />
                  <input
                    type="checkbox"
                    {...register("gstApply")}
                    disabled={payments?.length}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setValue("gstApply", !gstApply, { shouldDirty: true });
                      }
                    }}
                    aria-label="Apply GST"
                    className="h-3 w-3 absolute right-5 top-1/2 -translate-y-1/2 z-10
                     text-[#9481ce] border-gray-300 rounded focus:ring-[#9481ce] cursor-pointer"
                  />
                  <input
                    type="checkbox"
                    {...register("gstRoundOff")}
                    disabled
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setValue("gstRoundOff", !gstRoundOff, { shouldDirty: true });
                      }
                    }}
                    aria-label="Round off GST"
                    className="h-3 w-3 absolute right-1 top-1/2 -translate-y-1/2 z-10
                     text-[#9481ce] border-gray-300 rounded focus:ring-[#9481ce] cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Balance Metal (g)
                </label>
                <input
                  type="text"
                  disabled
                  value={threeDecimal(summaryTotals?.balanceMetal)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  TCS (0.1%) Amount (₹)
                </label>
                <input
                  type="text"
                  disabled
                  value={twoDecimal(summaryTotals?.tcsAmount)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Advance Amount
                </label>
                <input
                  type="text"
                  disabled
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1">
                  R.O.Discount
                </label>
                <input
                  type="text"
                  {...register("discount", { min: 0 })}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                    const isNumber = /[0-9]/.test(e.key);
                    const isDot = e.key === ".";
                    const alreadyHasDot = e.currentTarget.value.includes(".");
                    if (!isNumber && !allowedKeys.includes(e.key) && !(isDot && !alreadyHasDot)) {
                      e.preventDefault();
                    }
                  }}
                  className={`flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md focus:outline-none focus:ring-2 ${
                    invoiceNo ? "focus:ring-green-400" : "focus:ring-red-400"
                  }`}
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1">
                  Courier Charge
                </label>
                <input
                  type="text"
                  {...register("courierCharge", { min: 0 })}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                    const isNumber = /[0-9]/.test(e.key);
                    const isDot = e.key === ".";
                    const alreadyHasDot = e.currentTarget.value.includes(".");
                    if (!isNumber && !allowedKeys.includes(e.key) && !(isDot && !alreadyHasDot)) {
                      e.preventDefault();
                    }
                  }}
                  className={`flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md focus:outline-none focus:ring-2 ${
                    invoiceNo ? "focus:ring-green-400" : "focus:ring-red-400"
                  }`}
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Payable Amount
                </label>
                <input
                  type="text"
                  disabled
                  value={twoDecimal(summaryTotals?.payableAmount)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Paid Amount
                </label>
                <input
                  type="text"
                  disabled
                  value={twoDecimal(summaryTotals?.paidAmount)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>

              <div className="flex justify-center items-center">
                <label className="block text-[10px] font-medium text-[#4b3d91] flex-1 ">
                  Balance Payable
                </label>
                <input
                  type="text"
                  disabled
                  value={twoDecimal(summaryTotals?.balancePayable)}
                  className="flex-1 w-full px-2 py-1 text-[12px] font-medium border border-[#ccc] rounded-md"
                  placeholder="0.000"
                />
              </div>
            </div>
          </div>
        </div>

        {/* footer buttons */}
        <div className="flex justify-end mt-4 gap-2">
          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: "#7d6ec0" }}
            whileTap={{ scale: 0.95 }}
            type="button"
            className="flex items-center gap-2 bg-[#9481ce] text-white py-2.5 px-6 rounded-lg font-semibold shadow-md"
            onClick={() => {
              append({ totalWt: "", stones: [defaultStoneFields] });
              requestAnimationFrame(() => setTimeout(() => firstFieldRef.current?.focus(), 0));
            }}
          >
            <FaPlus className="text-base" />
            <span className="text-sm tracking-wide">Add</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: isLoading ? 1 : 1.05, backgroundColor: "#7d6ec0" }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            type="submit"
            disabled={isLoading}
            className="flex items-center gap-2 bg-[#9481ce] text-white py-2.5 px-6 rounded-lg font-semibold shadow-md disabled:opacity-60"
          >
            <FaCheck className="text-base" />
            <span className="text-sm tracking-wide">{isLoading ? "Saving..." : "Save"}</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: "#7d6ec0" }}
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={() => {
              reset(DEFAULTS);
              requestAnimationFrame(() => setTimeout(() => firstFieldRef.current?.focus(), 0));
            }}
            className="flex items-center gap-2 bg-[#9481ce] text-white py-2.5 px-6 rounded-lg font-semibold shadow-md"
          >
            <FaUndo className="text-base" />
            <span className="text-sm tracking-wide">Reset</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: "#7d6ec0" }}
            whileTap={{ scale: 0.95 }}
            type="button"
            className="flex items-center gap-2 bg-[#9481ce] text-white py-2.5 px-6 rounded-lg font-semibold shadow-md"
          >
            <FaList className="text-base" />
            <span className="text-sm tracking-wide">Labelled-Stock List</span>
          </motion.button>
        </div>
      </form>
    </>
  );
};

export default PurchaseStockForm;
